import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, LogOut, Settings, BarChart3, Instagram, Home, Users, Shield, HelpCircle, FileText, Phone, Crown, UserX, Share2, Coins, Plus, MessageCircle, Film, MessageSquareHeart } from 'lucide-react';
import { useAuthStore } from '../../stores/useAuthStore';
import { useTranslation } from '../../hooks/useLanguage';
import { supabase } from '../../lib/supabase';
import { NotificationOptOutItem } from './NotificationOptOutItem';
import { PushBellButton } from '../notifications/PushBellButton';
import { MobileMenuCallServices } from './MobileMenuCallServices';

const CompanyLogo: React.FC = () => (
    <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
        className="relative flex items-center gap-2 rounded-xl bg-black/40 px-2.5 py-2"
    >
        <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#0B0B0B] border border-[#1F1F1F]">
            <motion.span
                className="text-sm font-semibold tracking-[0.12em] text-white"
                initial={{ scale: 0.92 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.24, ease: 'easeOut' }}
            >
                SDL
            </motion.span>
            <div className="absolute inset-0 rounded-lg ring-1 ring-[#3182F6]/25" />
        </div>
        <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold text-white leading-tight tracking-wide">SocialDataLab</span>
            <span className="text-[10px] font-medium text-[#8A8A8A] leading-tight">Visitor Intelligence</span>
        </div>
    </motion.div>
);

const MenuToggleIcon: React.FC<{ open: boolean }> = ({ open }) => (
    <div className="relative flex h-4 w-5 items-center justify-center">
        <motion.span
            className="absolute left-0 h-[1.5px] w-full rounded-full bg-white"
            animate={open ? { rotate: 45, y: 5 } : { rotate: 0, y: -4 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
        />
        <motion.span
            className="absolute left-0 h-[1.5px] w-full rounded-full bg-white"
            animate={open ? { opacity: 0 } : { opacity: 0.7 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
        />
        <motion.span
            className="absolute left-0 h-[1.5px] w-full rounded-full bg-white"
            animate={open ? { rotate: -45, y: -5 } : { rotate: 0, y: 4 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
        />
    </div>
);

const Header: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState<boolean>(false);
    const [isVipUser, setIsVipUser] = useState<boolean>(false);
    const [creditBalance, setCreditBalance] = useState<number>(0);
    const [isLoadingCredits, setIsLoadingCredits] = useState<boolean>(true);
    // 리퍼럴 메뉴 노출 여부 (2026-10-01부터 로그인한 모두)
    const [hasReferral, setHasReferral] = useState<boolean>(false);
    const [hasInstagramAccount, setHasInstagramAccount] = useState<boolean>(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, signOut } = useAuthStore();
    const { t } = useTranslation();

    // 리퍼럴 메뉴는 로그인한 모두에게 보인다 (2026-10-01 모두에게 열린 리퍼럴 — 누구나 무료 참여)
    useEffect(() => {
        setHasReferral(Boolean(user?.id));
    }, [user?.id]);

    // 인스타 계정 연동 여부 — 스토리 조회 메뉴는 연동한 유저에게만 보여준다.
    // (크레딧이 오가는 기능이라 서비스 기본 관문인 계정 연동 뒤에 둔다 — 다니엘 지시 2026-07-30)
    useEffect(() => {
        if (!user) {
            setHasInstagramAccount(false);
            return;
        }
        void supabase
            .from('saved_instagram_accounts')
            .select('id')
            .eq('user_id', user.id)
            .limit(1)
            .then(({ data, error }) => {
                if (error) {
                    console.error('인스타 연동 상태 확인 오류:', error);
                    setHasInstagramAccount(false);
                    return;
                }
                setHasInstagramAccount((data?.length ?? 0) > 0);
            });
    }, [user]);

    // Check VIP status
    useEffect(() => {
        const checkVipStatus = async () => {
            if (user) {
                try {
                    const { data, error } = await supabase
                        .from('vip_users')
                        .select('vip_status')
                        .eq('user_id', user.id)
                        .maybeSingle();

                    // active 상태인지 확인
                    setIsVipUser(data?.vip_status === 'active');
                } catch (err) {
                    console.error('VIP 상태 확인 오류:', err);
                    setIsVipUser(false);
                }
            }
        };
        checkVipStatus();
    }, [user]);

    // Fetch credit balance - 실시간 업데이트 지원
    useEffect(() => {
        if (!user) {
            setCreditBalance(0);
            setIsLoadingCredits(false);
            return;
        }

        // 크레딧 잔액 조회 함수
        const fetchCreditBalance = async () => {
            setIsLoadingCredits(true);
            try {
                const { data, error } = await supabase
                    .from('user_credits')
                    .select('balance')
                    .eq('user_id', user.id)
                    .maybeSingle();

                if (error) {
                    console.error('크레딧 잔액 조회 오류:', error);
                    setCreditBalance(0);
                } else {
                    setCreditBalance(data?.balance ?? 0);
                }
            } catch (err) {
                console.error('크레딧 잔액 조회 실패:', err);
                setCreditBalance(0);
            } finally {
                setIsLoadingCredits(false);
            }
        };

        // 초기 데이터 조회
        fetchCreditBalance();

        // Supabase Realtime 구독 - 크레딧 변경 실시간 감지
        const channel = supabase
            .channel(`user_credits_${user.id}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'user_credits',
                    filter: `user_id=eq.${user.id}`
                },
                (payload) => {
                    console.log('💰 크레딧 변경 감지:', payload);
                    if (payload.new && 'balance' in payload.new) {
                        setCreditBalance((payload.new as { balance: number }).balance);
                    }
                }
            )
            .subscribe();

        // 브라우저 탭 포커스 시 다시 조회
        const handleFocus = () => {
            fetchCreditBalance();
        };
        window.addEventListener('focus', handleFocus);

        // 클린업
        return () => {
            supabase.removeChannel(channel);
            window.removeEventListener('focus', handleFocus);
        };
    }, [user]);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMenuOpen]);

    const handleSignOut = async (): Promise<void> => {
        try {
            await signOut();
            navigate('/');
        } catch (error: any) {
            // AuthSessionMissingError는 무시 (이미 로그아웃된 상태)
            if (error?.name === 'AuthSessionMissingError') {
                // 로컬 세션 클리어하고 홈으로 이동
                localStorage.removeItem('sb-zjfhxnrquesjysgqpqbl-auth-token');
                navigate('/');
            } else {
                console.error('Sign out failed:', error);
            }
        }
    };

    const handleDesktopSignOut = async () => {
        setIsUserMenuOpen(false);
        await handleSignOut();
    };

    const handleMobileSignOut = async () => {
        setIsMenuOpen(false);
        await handleSignOut();
    };

    const isActive = (path: string): boolean => location.pathname === path;

    const navItems = user ? [
        { path: '/dashboard', label: t('nav.dashboard'), icon: Home },
        { path: '/connected-account', label: '연결된 계정', icon: Instagram },
        { path: '/analytics', label: '분석 내역', icon: BarChart3 },
        // 스토리 조회는 인스타 계정을 연동한 유저에게만 (데스크톱·모바일 메뉴 공통)
        ...(hasInstagramAccount ? [{ path: '/story-viewer', label: '스토리 조회', icon: Film }] : []),
        { path: '/chat', label: '대화 시뮬레이터', icon: MessageCircle },
    ] : [
        { path: '/', label: t('nav.home'), icon: Home }
    ];

    // Design System: Additional navigation items for mobile
    const additionalItems = [
        // 후기 쓰기 — 로그인한 손님만. '12크레딧 받기' 선전 문구는 뺌(대표 10/29 "그 후기 쓰면 12크레딧 선전문구는 빼자")
        ...(user ? [{ path: '/review', label: '후기 쓰기', icon: MessageSquareHeart }] : []),
        { path: '/contact', label: '문의하기', icon: Phone },
    ];

    return (
        <>
            <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-filter backdrop-blur-xl border-b border-[#262626] pt-[env(safe-area-inset-top)]">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-14 sm:h-16">
                        {/* Logo - ThreadPulse Design System */}
                        <Link
                            to={user ? "/dashboard" : "/"}
                            className="inline-flex min-w-0 items-center"
                        >
                            <CompanyLogo />
                        </Link>

                        {/* Desktop Navigation - ThreadPulse Design */}
                        <nav className="hidden lg:flex items-center gap-1">
                            {navItems.map((item) => (
                                <motion.div
                                    key={item.path}
                                    whileHover={{ y: -2 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                >
                                    <Link
                                        to={item.path}
                                        className={`relative px-4 py-2.5 rounded-2xl text-sm font-medium transition-all duration-300 ${isActive(item.path)
                                                ? 'bg-[#3182F6]/10 text-[#3182F6] shadow-[0_0_30px_rgba(49,130,246,0.3)]'
                                                : 'text-[#A8A8A8] hover:text-white hover:bg-[#181818]'
                                            }`}
                                    >
                                        <span className="relative z-10">{item.label}</span>
                                        {isActive(item.path) && (
                                            <motion.div
                                                layoutId="navbar-indicator"
                                                className="absolute inset-0 bg-[#3182F6]/5 rounded-2xl border border-[#3182F6]/20"
                                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                            />
                                        )}
                                    </Link>
                                </motion.div>
                            ))}
                        </nav>

                        {/* Desktop Actions - ThreadPulse Design */}
                        <div className="hidden lg:flex items-center gap-3">
                            {/* 실시간 알림 벨 — 미구독이면 빨간 점 상시 표시 */}
                            {user && <PushBellButton />}
                            {/* User Menu */}
                            {user ? (
                                <div className="relative">
                                    <motion.button
                                        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                        className="group flex items-center gap-2 px-3 py-2 rounded-2xl bg-[#181818] border border-[#262626] hover:border-[#363636] transition-all duration-300 hover:shadow-[0_0_30px_rgba(49,130,246,0.15)]"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <div className="relative w-8 h-8 rounded-xl overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-[#3182F6] to-[#4FC3F7]" />
                                            <div className="absolute inset-[2px] rounded-[10px] bg-[#181818] flex items-center justify-center">
                                                <User className="w-4 h-4 text-white" />
                                            </div>
                                        </div>
                                        <span className="text-sm text-white font-medium">
                                            {user.email?.split('@')[0]}
                                        </span>
                                        <motion.svg
                                            animate={{ rotate: isUserMenuOpen ? 180 : 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="w-4 h-4 text-[#777777]"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </motion.svg>
                                    </motion.button>

                                    <AnimatePresence>
                                        {isUserMenuOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                                className="absolute right-0 top-14 w-64 bg-black/90 backdrop-filter backdrop-blur-[20px] rounded-2xl border border-[#262626]/60 shadow-[0_10px_40px_rgba(0,0,0,0.8)] overflow-hidden"
                                            >
                                                <div className="p-4 border-b border-[#262626]/60">
                                                    <div className="flex items-center gap-3">
                                                        <div className="relative w-10 h-10 rounded-xl overflow-hidden">
                                                            <div className="absolute inset-0 bg-gradient-to-r from-[#3182F6] to-[#4FC3F7]" />
                                                            <div className="absolute inset-[2px] rounded-[10px] bg-[#181818] flex items-center justify-center">
                                                                <User className="w-5 h-5 text-white" />
                                                            </div>
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-sm font-semibold text-white">
                                                                {user.email?.split('@')[0] || 'User'}
                                                            </p>
                                                            <p className="text-xs text-[#777777] mt-0.5">{user.email}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 크레딧 잔액 섹션 - Toss Design System */}
                                                <div className="p-3 border-b border-[#262626]/60">
                                                    <Link
                                                        to="/credit"
                                                        className="group flex items-center justify-between p-3 rounded-xl bg-[#f9fafb]/5 hover:bg-[#3182f6]/10 transition-all duration-200"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-xl bg-[#3182f6] flex items-center justify-center">
                                                                <Coins className="w-5 h-5 text-white" />
                                                            </div>
                                                            <div>
                                                                <p className="text-[11px] font-medium text-[#8b95a1] uppercase tracking-wider">보유 크레딧</p>
                                                                <p className="text-lg font-bold text-white">
                                                                    {isLoadingCredits ? (
                                                                        <span className="inline-block w-8 h-5 bg-[#262626] rounded animate-pulse" />
                                                                    ) : (
                                                                        <>{creditBalance.toLocaleString()} <span className="text-sm font-medium text-[#8b95a1]">C</span></>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#3182f6] group-hover:bg-[#1b64da] transition-colors duration-200">
                                                            <Plus className="w-3.5 h-3.5 text-white" />
                                                            <span className="text-xs font-semibold text-white">충전</span>
                                                        </div>
                                                    </Link>
                                                </div>

                                                <div className="p-2">
                                                    {isVipUser && (
                                                        <Link
                                                            to="/vip-dashboard"
                                                            className="group flex items-center gap-3 px-3 py-2.5 text-sm rounded-2xl transition-all duration-300 bg-gradient-to-r from-[#FFB800]/10 to-[#FFB800]/5 hover:from-[#FFB800]/20 hover:to-[#FFB800]/10"
                                                            onClick={() => setIsUserMenuOpen(false)}
                                                        >
                                                            <div className="relative w-8 h-8 rounded-lg overflow-hidden">
                                                                <div className="absolute inset-0 bg-gradient-to-r from-[#FFB800] to-[#FFB800]" />
                                                                <div className="absolute inset-[2px] rounded-md bg-[#181818] flex items-center justify-center">
                                                                    <Crown className="w-4 h-4 text-[#FFB800]" />
                                                                </div>
                                                            </div>
                                                            <span className="text-[#FFB800] font-medium">VIP 대시보드</span>
                                                        </Link>
                                                    )}
                                                    <Link
                                                        to="/connected-account"
                                                        className="group flex items-center gap-3 px-3 py-2.5 text-sm text-[#A1A1AA] hover:text-[#FAFAFA] rounded-xl hover:bg-[#27272A]/50 transition-all duration-300"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <div className="w-8 h-8 rounded-lg bg-[#27272A] group-hover:bg-[#3F3F46] flex items-center justify-center transition-colors duration-300">
                                                            <Instagram className="w-4 h-4" />
                                                        </div>
                                                        <span className="font-medium">연결된 계정</span>
                                                    </Link>
                                                    <Link
                                                        to="/analytics"
                                                        className="group flex items-center gap-3 px-3 py-2.5 text-sm text-[#A1A1AA] hover:text-[#FAFAFA] rounded-xl hover:bg-[#27272A]/50 transition-all duration-300"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <div className="w-8 h-8 rounded-lg bg-[#27272A] group-hover:bg-[#3F3F46] flex items-center justify-center transition-colors duration-300">
                                                            <BarChart3 className="w-4 h-4" />
                                                        </div>
                                                        <span className="font-medium">분석 내역</span>
                                                    </Link>
                                                    <Link
                                                        to="/chat"
                                                        className="group flex items-center gap-3 px-3 py-2.5 text-sm text-[#A1A1AA] hover:text-[#FAFAFA] rounded-xl hover:bg-[#27272A]/50 transition-all duration-300"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <div className="w-8 h-8 rounded-lg bg-[#27272A] group-hover:bg-[#3F3F46] flex items-center justify-center transition-colors duration-300">
                                                            <MessageCircle className="w-4 h-4" />
                                                        </div>
                                                        <span className="font-medium">대화 시뮬레이터</span>
                                                    </Link>
                                                    {/* 리퍼럴 프로그램 - 로그인한 모두 (2026-10-01 무료 개방) */}
                                                    {hasReferral && (
                                                        <Link
                                                            to="/referral-dashboard"
                                                            className="group flex items-center gap-3 px-3 py-2.5 text-sm text-[#A1A1AA] hover:text-[#FAFAFA] rounded-xl hover:bg-[#27272A]/50 transition-all duration-300"
                                                            onClick={() => setIsUserMenuOpen(false)}
                                                        >
                                                            <div className="w-8 h-8 rounded-lg bg-[#27272A] group-hover:bg-[#3F3F46] flex items-center justify-center transition-colors duration-300">
                                                                <Users className="w-4 h-4" />
                                                            </div>
                                                            <span className="font-medium">리퍼럴 프로그램</span>
                                                        </Link>
                                                    )}
                                                    {/* 후기 쓰기 — '12크레딧 받기' 선전 문구는 뺌(대표 10/29) */}
                                                    <Link
                                                        to="/review"
                                                        className="group flex items-center gap-3 px-3 py-2.5 text-sm text-[#A1A1AA] hover:text-[#FAFAFA] rounded-xl hover:bg-[#27272A]/50 transition-all duration-300"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <div className="w-8 h-8 rounded-lg bg-[#27272A] group-hover:bg-[#3F3F46] flex items-center justify-center transition-colors duration-300">
                                                            <MessageSquareHeart className="w-4 h-4" />
                                                        </div>
                                                        <span className="font-medium">후기 쓰기</span>
                                                    </Link>
                                                    <NotificationOptOutItem variant="desktop" />
                                                </div>

                                                <div className="border-t border-[#27272A] p-2">
                                                    <Link
                                                        to="/account-deletion"
                                                        className="group flex items-center gap-3 px-3 py-2.5 text-sm text-[#A1A1AA] hover:text-[#FAFAFA] rounded-xl hover:bg-[#27272A]/50 transition-all duration-300 w-full"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <div className="w-8 h-8 rounded-lg bg-[#27272A] group-hover:bg-[#3F3F46] flex items-center justify-center transition-colors duration-300">
                                                            <UserX className="w-4 h-4" />
                                                        </div>
                                                        <span className="font-medium">탈퇴 및 데이터 파기</span>
                                                    </Link>
                                                    <button
                                                        type="button"
                                                        onClick={handleDesktopSignOut}
                                                        className="group flex items-center gap-3 px-3 py-2.5 text-sm rounded-xl transition-all duration-300 w-full hover:bg-[#EF4444]/10"
                                                    >
                                                        <div className="w-8 h-8 rounded-lg bg-[#EF4444]/10 group-hover:bg-[#EF4444]/20 flex items-center justify-center transition-colors duration-300">
                                                            <LogOut className="w-4 h-4 text-[#EF4444]" />
                                                        </div>
                                                        <span className="text-[#EF4444] font-medium">로그아웃</span>
                                                    </button>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <Link
                                        to="/auth"
                                        className="px-4 py-2 text-sm text-[#A8A8A8] hover:text-white font-medium transition-all duration-300"
                                    >
                                        {t('nav.auth')}
                                    </Link>
                                    <Link
                                        to="/auth"
                                        className="group relative px-5 py-2.5 bg-gradient-to-r from-[#3182F6] to-[#4FC3F7] text-white rounded-full text-sm font-semibold transition-all duration-300 shadow-[0_0_30px_rgba(49,130,246,0.3)] hover:shadow-[0_0_40px_rgba(49,130,246,0.5)]"
                                    >
                                        <span className="relative z-10">{t('cta.start')}</span>
                                        <div className="absolute inset-0 bg-gradient-to-r from-[#3182F6] to-[#4FC3F7] rounded-full opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300" />
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* 모바일 우측 그룹 — 벨과 메뉴 버튼이 항상 나란히 붙어 있게 */}
                        <div className="flex items-center gap-2 lg:hidden">
                            {user && <PushBellButton variant="mobile" />}

                            {/* Mobile/Tablet Menu Button - Improved Design */}
                            <motion.button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                aria-label={isMenuOpen ? '메뉴 닫기' : '메뉴 열기'}
                                className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-[#1F1F1F] bg-black/70 shadow-[0_8px_24px_rgba(0,0,0,0.45)] transition-all duration-250 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3182F6]/45"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#3182F6]/15 via-transparent to-[#4FC3F7]/15 opacity-0 transition-opacity duration-200 hover:opacity-100" />
                                <div className="absolute inset-[1px] rounded-[10px] border border-[#262626]" />
                                <MenuToggleIcon open={isMenuOpen} />
                            </motion.button>
                        </div>
                    </div>

                </div>
            </header>

            {/* Mobile Menu Overlay - ThreadPulse Design */}
            <AnimatePresence>
                {isMenuOpen && (
                    <>
                        {/* Backdrop with improved blur */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="fixed inset-0 bg-black/40 backdrop-filter backdrop-blur-sm z-[9998] lg:hidden"
                            onClick={() => setIsMenuOpen(false)}
                        />

                        {/* Mobile Menu Slide Panel - Enhanced Design */}
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                            className="fixed top-0 right-0 bottom-0 w-[280px] sm:w-[320px] md:w-[380px] bg-black backdrop-filter backdrop-blur-2xl border-l border-[#262626] shadow-2xl z-[9999] lg:hidden overflow-y-auto pb-[env(safe-area-inset-bottom)]"
                        >
                            {/* Mobile Menu Header - Enhanced Design (standalone 상태바 안전영역 보정) */}
                            <div className="sticky top-0 bg-black/95 backdrop-blur-xl z-10 flex items-center justify-between p-4 sm:p-5 pt-[max(1rem,env(safe-area-inset-top))] sm:pt-[max(1.25rem,env(safe-area-inset-top))] border-b border-[#262626]">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-gradient-to-br from-[#3182F6] to-[#4FC3F7] rounded-xl flex items-center justify-center">
                                        <Menu className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-base sm:text-lg font-bold text-white">메뉴</span>
                                </div>
                                <motion.button
                                    onClick={() => setIsMenuOpen(false)}
                                    aria-label="메뉴 닫기"
                                    className="relative w-11 h-11 flex items-center justify-center rounded-xl bg-[#101010] border border-[#262626] hover:border-[#3182F6]/30 transition-all duration-300 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3182F6]/40"
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-[#3182F6]/20 to-[#4FC3F7]/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <X className="w-5 h-5 text-[#3182F6] relative z-10" />
                                </motion.button>
                            </div>

                            {/* 크레딧 잔액 섹션 (모바일) - Toss Design System Compact */}
                            {user && (
                                <div className="px-4 py-2.5 border-b border-[#262626]">
                                    <Link
                                        to="/credit"
                                        className="group flex items-center justify-between px-3 py-2.5 rounded-xl bg-[#1a1a1a] hover:bg-[#222] transition-all duration-200"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-9 h-9 rounded-lg bg-[#3182f6] flex items-center justify-center">
                                                <Coins className="w-4.5 h-4.5 text-white" />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-medium text-[#6b7684]">보유 크레딧</p>
                                                <p className="text-base font-bold text-white leading-tight">
                                                    {isLoadingCredits ? (
                                                        <span className="inline-block w-8 h-4 bg-[#262626] rounded animate-pulse" />
                                                    ) : (
                                                        <>{creditBalance.toLocaleString()}</>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#3182f6] group-hover:bg-[#1b64da] transition-colors duration-200">
                                            <Plus className="w-3.5 h-3.5 text-white" />
                                            <span className="text-xs font-semibold text-white">충전</span>
                                        </div>
                                    </Link>
                                </div>
                            )}

                            {/* Navigation Items - Toss Design System */}
                            <div className="px-4 py-3">
                                <nav className="space-y-1">
                                    {navItems.map((item) => (
                                        <React.Fragment key={item.path}>
                                            <Link
                                                to={item.path}
                                                className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${isActive(item.path)
                                                        ? 'bg-[#3182f6]/10 text-[#3182f6]'
                                                        : 'text-[#8b95a1] hover:text-white hover:bg-[#1a1a1a]'
                                                    }`}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${isActive(item.path)
                                                        ? 'bg-[#3182f6]'
                                                        : 'bg-[#1a1a1a]'
                                                    }`}>
                                                    <item.icon className="w-4 h-4 text-white" />
                                                </div>
                                                <span className="text-[14px] font-medium">{item.label}</span>
                                            </Link>
                                            {/* 리퍼럴 프로그램 - 로그인한 모두 (2026-10-01 무료 개방) */}
                                            {item.path === '/analytics' && hasReferral && (
                                                <Link
                                                    to="/referral-dashboard"
                                                    className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${isActive('/referral-dashboard')
                                                            ? 'bg-[#3182f6]/10 text-[#3182f6]'
                                                            : 'text-[#8b95a1] hover:text-white hover:bg-[#1a1a1a]'
                                                        }`}
                                                    onClick={() => setIsMenuOpen(false)}
                                                >
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${isActive('/referral-dashboard')
                                                            ? 'bg-[#3182f6]'
                                                            : 'bg-[#1a1a1a]'
                                                        }`}>
                                                        <Users className="w-4 h-4 text-white" />
                                                    </div>
                                                    <span className="text-[14px] font-medium">리퍼럴 프로그램</span>
                                                </Link>
                                            )}
                                        </React.Fragment>
                                    ))}

                                    {/* VIP Dashboard */}
                                    {isVipUser && (
                                        <Link
                                            to="/vip-dashboard"
                                            className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${isActive('/vip-dashboard')
                                                    ? 'bg-[#FFB800]/10'
                                                    : 'hover:bg-[#1a1a1a]'
                                                }`}
                                            onClick={() => setIsMenuOpen(false)}
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FFB800] to-[#FF8C00] flex items-center justify-center">
                                                <Crown className="w-4 h-4 text-white" />
                                            </div>
                                            <span className="text-[14px] font-medium text-[#FFB800]">VIP 대시보드</span>
                                        </Link>
                                    )}
                                </nav>

                                {/* 블랭킷 전화 상담 8개 — 대표 7964(2026-10-22) '왼쪽 메뉴 화면, 거기 내용에도 상세하게' */}
                                <MobileMenuCallServices onNavigate={() => setIsMenuOpen(false)} />

                                {/* Additional Links - Toss Style */}
                                <div className="mt-4 pt-4 border-t border-[#262626]">
                                    <h3 className="text-[11px] font-semibold text-[#6b7684] uppercase tracking-wider mb-2 px-3">더보기</h3>
                                    <nav className="space-y-1">
                                        {additionalItems.map((item) => (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${isActive(item.path)
                                                        ? 'bg-[#1a1a1a] text-white'
                                                        : 'text-[#8b95a1] hover:text-white hover:bg-[#1a1a1a]'
                                                    }`}
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <div className="w-8 h-8 rounded-lg bg-[#1a1a1a] border border-[#333d4b] flex items-center justify-center transition-all duration-200">
                                                    <item.icon className="w-4 h-4" />
                                                </div>
                                                <span className="text-[14px] font-medium">{item.label}</span>
                                            </Link>
                                        ))}

                                        <NotificationOptOutItem variant="mobile" />
                                    </nav>
                                </div>

                                {/* Auth Actions - Toss Style */}
                                <div className="mt-4 pt-4 border-t border-[#262626]">
                                    {user ? (
                                        <div className="space-y-3">
                                            <button
                                                type="button"
                                                onClick={handleMobileSignOut}
                                                className="group flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-[#f04452]/10 hover:bg-[#f04452]/20 transition-all duration-200"
                                            >
                                                <LogOut className="w-4 h-4 text-[#f04452]" />
                                                <span className="text-[13px] font-medium text-[#f04452]">로그아웃</span>
                                            </button>
                                            {/* 파괴적 액션은 버튼이 아닌 저강조 텍스트 링크로 — 오터치 방지 */}
                                            <Link
                                                to="/account-deletion"
                                                className="block text-center text-[11px] text-[#6b7684] hover:text-[#8b95a1] underline underline-offset-2 transition-colors"
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                탈퇴 및 데이터 파기
                                            </Link>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <Link
                                                to="/auth"
                                                className="group relative block w-full px-4 py-3 bg-gradient-to-r from-[#3182F6] to-[#4FC3F7] text-white rounded-full text-sm font-semibold text-center transition-all duration-300 shadow-[0_0_30px_rgba(49,130,246,0.3)]"
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                <span className="relative z-10">{t('cta.start')}</span>
                                                <div className="absolute inset-0 bg-gradient-to-r from-[#3182F6] to-[#4FC3F7] rounded-full opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300" />
                                            </Link>
                                            <Link
                                                to="/auth"
                                                className="block w-full px-4 py-3 bg-[#181818] border border-[#262626] text-white rounded-2xl text-sm font-medium text-center hover:bg-[#262626] hover:border-[#363636] transition-all duration-300"
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                {t('nav.auth')}
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer Info - Enhanced Design */}
                            <div className="p-4 mt-auto bg-[#101010] border-t border-[#262626]">
                                <div className="flex items-center justify-center gap-2">
                                    <div className="w-6 h-6 bg-gradient-to-br from-[#3182F6] to-[#4FC3F7] rounded-lg flex items-center justify-center">
                                        <Instagram className="w-3 h-3 text-white" />
                                    </div>
                                    <p className="text-xs text-[#777777]">
                                        © 2026 SocialDataLab
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default Header; 
